/* eslint-disable no-use-before-define */
// @flow
import { curry } from "ramda";

import { openModal } from "@quantum/ui";
import { type CurriedFn2 } from "@quantum/utils";

import type {
  Id,
  GrupamentosMedida,
  GridStateSaveTypes,
  TipoListaPeriodo,
  MedidaSelecionada,
  MedidaParaPersonalizar,
  MedidaUsuarioTipo,
  DatasSelecaoJanela,
  AtivoSelecionado,
  ArquivoMedida,
  OperacaoArquivos,
  ModoApp,
  AssetKey,
  CurrentUser,
  ExportType,
  Report,
  ReportId,
  ConfiguracaoGlobalRelatorio,
} from "../model";
import type Moment from "moment";
import {
  type DetalhesUsuario,
  type GeradorMedidasSelecionadas,
  type GridState,
  type ModoDeBuscaDeAtivoExcel,
} from "../state";

export const alerta = (mensagem: string) =>
  openModal("ModalAlerta", { mensagem });

export const alertaErro = (mensagem: string) =>
  openModal("ModalAlerta", { mensagem, style: "error" });

type CarregarAppAction = {
  type: "CARREGAR_APP",
  abrirTemplate: ?string,
  restaurarCache: ?boolean,
  idMedidaUsuario: ?string,
};

type BootConfig = {
  abrirTemplate: ?string,
  restaurarCache: ?boolean,
  contarPageViewConfiguracao: ?Boolean,
  idMedidaUsuario: ?string,
};

export function carregarApp({
  abrirTemplate = null,
  restaurarCache = true,
  contarPageViewConfiguracao = true,
  idMedidaUsuario = null,
}: BootConfig = {}): CarregarAppAction {
  return {
    type: "CARREGAR_APP",
    abrirTemplate,
    restaurarCache,
    contarPageViewConfiguracao,
    idMedidaUsuario,
  };
}

type IniciarAppAction = {
  type: "INICIAR_APP",
};

export function iniciarApp(): IniciarAppAction {
  return { type: "INICIAR_APP" };
}

type DefinirModoAppAction = {
  type: "DEFINIR_MODO_APP",
  payload: ModoApp,
};

export const definirModoApp = (modo: ModoApp): DefinirModoAppAction => ({
  type: "DEFINIR_MODO_APP",
  payload: modo,
});

type SetErroInicializacaoAction = {
  type: "SET_ERRO_INICIALIZACAO",
  erro: string,
};

export function setErroInicializacao(erro: string): SetErroInicializacaoAction {
  return { type: "SET_ERRO_INICIALIZACAO", erro };
}

type SetSeletorAbertoAction = {
  type: "SET_SELETOR_ABERTO",
  payload: { id: string, aberto: boolean },
};

export const setSeletorAberto = (
  id: string,
  aberto: boolean
): SetSeletorAbertoAction => ({
  type: "SET_SELETOR_ABERTO",
  payload: { id, aberto },
});

type SelecionarMedidaAction = {
  type: "SELECIONAR_MEDIDA",
  idMedidaSelecionada: Id | Array<Id>,
};

export function selecionarMedida(
  idMedidaSelecionada: Id | Array<Id>
): SelecionarMedidaAction {
  return { type: "SELECIONAR_MEDIDA", idMedidaSelecionada };
}

type SelecionarMedidaUsuarioAction = {
  type: "SELECIONAR_MEDIDA_USUARIO",
  idMedidaUsuario: Id | Array<Id>,
};

export function selecionarMedidaUsuario(
  idMedidaUsuario: Id | Array<Id>
): SelecionarMedidaUsuarioAction {
  return { type: "SELECIONAR_MEDIDA_USUARIO", idMedidaUsuario };
}

type AdicionarMedidaParaPersonalizar = {
  type: "ADICIONAR_MEDIDA_PARA_PERSONALIZAR",
  medidaParaPersonalizar: MedidaParaPersonalizar,
};

export function adicionarMedidaParaPersonalizar(
  medidaParaPersonalizar: MedidaParaPersonalizar
): AdicionarMedidaParaPersonalizar {
  return {
    type: "ADICIONAR_MEDIDA_PARA_PERSONALIZAR",
    medidaParaPersonalizar,
  };
}

type AdicionarMedidasParaPersonalizar = {
  type: "ADICIONAR_MEDIDAS_PARA_PERSONALIZAR",
  medidasParaPersonalizar: Array<MedidaParaPersonalizar>,
};

export function adicionarMedidasParaPersonalizar(
  medidasParaPersonalizar: Array<MedidaParaPersonalizar>
): AdicionarMedidasParaPersonalizar {
  return {
    type: "ADICIONAR_MEDIDAS_PARA_PERSONALIZAR",
    medidasParaPersonalizar,
  };
}

type RemoverMedidaParaPersonalizar = {
  type: "REMOVER_MEDIDA_PARA_PERSONALIZAR",
  idMedidaParaPersonalizar: string,
};

export function removerMedidaParaPersonalizar(
  idMedidaParaPersonalizar: string
): RemoverMedidaParaPersonalizar {
  return { type: "REMOVER_MEDIDA_PARA_PERSONALIZAR", idMedidaParaPersonalizar };
}

export type CarregarDominioAction = {
  type: "CARREGAR_DOMINIO",
  dominio: any,
};

export function carregarDominioSuccess(dominio: any): CarregarDominioAction {
  return { type: "CARREGAR_DOMINIO", dominio };
}

type CarregarMedidasUsuarioAction = {
  type: "CARREGAR_MEDIDAS_USUARIO",
  medidasUsuario: Array<MedidaUsuarioTipo>,
};

export function carregarMedidasUsuario(
  medidasUsuario: Array<MedidaUsuarioTipo>
): CarregarMedidasUsuarioAction {
  return { type: "CARREGAR_MEDIDAS_USUARIO", medidasUsuario };
}

type AtualizarMedidasUsuarioAction = {
  type: "ATUALIZAR_MEDIDAS_USUARIO",
};

export const atualizarMedidasUsuario = (): AtualizarMedidasUsuarioAction => ({
  type: "ATUALIZAR_MEDIDAS_USUARIO",
});

type AbrirModalMedidaUsuarioAction = {
  type: "ABRIR_MODAL_MEDIDA_USUARIO",
};

export const abrirModalMedidaUsuario = (): AbrirModalMedidaUsuarioAction => ({
  type: "ABRIR_MODAL_MEDIDA_USUARIO",
});

type AdicionarMedidaUsuarioAction = {
  type: "ADICIONAR_MEDIDA_USUARIO",
  medidasSelecionadas: Array<MedidaSelecionada>,
};

export function adicionarMedidaUsuario(
  medidasSelecionadas: Array<MedidaSelecionada>
): AdicionarMedidaUsuarioAction {
  return {
    type: "ADICIONAR_MEDIDA_USUARIO",
    medidasSelecionadas,
  };
}

type ExcluirMedidaUsuarioAction = {
  type: "EXCLUIR_MEDIDA_USUARIO",
  idMedidaUsuario: Id,
};

export function excluirMedidaUsuario(
  idMedidaUsuario: Id
): ExcluirMedidaUsuarioAction {
  return { type: "EXCLUIR_MEDIDA_USUARIO", idMedidaUsuario };
}

type ImportarMedidaUsuarioAction = {
  type: "IMPORTAR_MEDIDA_USUARIO",
  idMedidaUsuario: Id,
};

export function importarMedidaUsuario(
  idMedidaUsuario: Id
): ImportarMedidaUsuarioAction {
  return { type: "IMPORTAR_MEDIDA_USUARIO", idMedidaUsuario };
}

type SelecionarPeriodoAction = {
  type: "SELECIONAR_PERIODO",
  periodosSelecionados: Array<Id>,
};

export function selecionarPeriodo(
  periodosSelecionados: Array<Id>
): SelecionarPeriodoAction {
  return { type: "SELECIONAR_PERIODO", periodosSelecionados };
}

type SelecionarAjusteDiasAction = {
  type: "SELECIONAR_AJUSTE_DIAS",
  ajusteDiasSelecionado: Id,
};

export function selecionarAjusteDias(
  ajusteDiasSelecionado: Id
): SelecionarAjusteDiasAction {
  return { type: "SELECIONAR_AJUSTE_DIAS", ajusteDiasSelecionado };
}

type SelecionarFonteFidcAction = {
  type: "SELECIONAR_FONTE_FIDC",
  fonteFidcSelecionada: Id,
};

export function selecionarFonteFidc(
  fonteFidcSelecionada: Id
): SelecionarFonteFidcAction {
  return { type: "SELECIONAR_FONTE_FIDC", fonteFidcSelecionada };
}

type SelecionarExibirConfiguracoesAvancadasAction = {
  type: "SELECIONAR_EXIBIR_CONFIGURACOES_AVANCADAS",
  exibirConfiguracoesAvancadas: boolean,
};

export function selecionarExibirConfiguracoesAvancadas(
  exibirConfiguracoesAvancadas: boolean
): SelecionarExibirConfiguracoesAvancadasAction {
  return {
    type: "SELECIONAR_EXIBIR_CONFIGURACOES_AVANCADAS",
    exibirConfiguracoesAvancadas,
  };
}

type SelecionarJanelaMovelAction = {
  type: "SELECIONAR_JANELA_MOVEL",
  janelaMovel: number,
};

export function selecionarJanelaMovel(
  janelaMovel: number
): SelecionarJanelaMovelAction {
  return {
    type: "SELECIONAR_JANELA_MOVEL",
    janelaMovel,
  };
}

type SelecionarPeriodicidadeAction = {
  type: "SELECIONAR_PERIODICIDADE",
  periodicidadeSelecionada: Id,
};

export function selecionarPeriodicidade(
  periodicidadeSelecionada: Id
): SelecionarPeriodicidadeAction {
  return {
    type: "SELECIONAR_PERIODICIDADE",
    periodicidadeSelecionada,
  };
}

type SelecionarTipoListaPeriodosAction = {
  type: "SELECIONAR_TIPO_LISTA_PERIODOS",
  tipoListaPeriodo: TipoListaPeriodo,
};

export function selecionarTipoListaPeriodos(
  tipoListaPeriodo: TipoListaPeriodo
): SelecionarTipoListaPeriodosAction {
  return { type: "SELECIONAR_TIPO_LISTA_PERIODOS", tipoListaPeriodo };
}

type SelecionarUltimaNegociacaoMesAction = {
  type: "SELECIONAR_ULTIMA_NEGOCIACAO_MES",
  payload: Boolean,
};

export function selecionarUltimaNegociacaoMes(
  isUltimaNegociacaoMes: Boolean
): SelecionarUltimaNegociacaoMesAction {
  return {
    type: "SELECIONAR_ULTIMA_NEGOCIACAO_MES",
    payload: isUltimaNegociacaoMes,
  };
}

type SelecionarAdicaoDataReferenciaAction = {
  type: "SELECIONAR_ADICAO_DATA_REFERENCIA",
  payload: Boolean,
};

export function selecionarAdicaoDataReferencia(
  adicionarDataReferencia: Boolean
): SelecionarAdicaoDataReferenciaAction {
  return {
    type: "SELECIONAR_ADICAO_DATA_REFERENCIA",
    payload: adicionarDataReferencia,
  };
}

export type AdicionarMedidaAction = {
  type: "ADICIONAR_MEDIDA",
  medidasSelecionadas: Array<MedidaSelecionada>,
  substituirSelecao: boolean,
  posicao: number,
  novaMedida: boolean,
};

export function adicionarMedidas(
  medidasSelecionadas: Array<MedidaSelecionada>,
  substituirSelecao: boolean = false,
  posicao: number = -1,
  novaMedida: boolean = false
): AdicionarMedidaAction {
  return {
    type: "ADICIONAR_MEDIDA",
    medidasSelecionadas,
    substituirSelecao,
    posicao,
    novaMedida,
  };
}

type AdicionarMedidaQualitativaAction = {
  type: "ADICIONAR_MEDIDA_QUALITATIVA",
  idMedidaSelecionada: Id,
  geradorMedidasSelecionadas: GeradorMedidasSelecionadas,
  actionCreatorAdicionar: any => Action,
};

export function adicionarMedidaQualitativa(
  idMedidaSelecionada: *,
  geradorMedidasSelecionadas: *,
  actionCreatorAdicionar: *
): AdicionarMedidaQualitativaAction {
  return {
    type: "ADICIONAR_MEDIDA_QUALITATIVA",
    idMedidaSelecionada,
    geradorMedidasSelecionadas,
    actionCreatorAdicionar,
  };
}

type SelecionarTipoContaAction = {
  type: "SELECIONAR_TIPO_CONTA",
  tipoContaSelecionado: Id,
};

export function selecionarTipoConta(
  tipoContaSelecionado: Id
): SelecionarTipoContaAction {
  return {
    type: "SELECIONAR_TIPO_CONTA",
    tipoContaSelecionado,
  };
}

type SelecionarVersaoContaAction = {
  type: "SELECIONAR_VERSAO_CONTA",
  versaoContaSelecionada: Id,
};

export function selecionarVersaoConta(
  versaoContaSelecionada: Id
): SelecionarVersaoContaAction {
  return {
    type: "SELECIONAR_VERSAO_CONTA",
    versaoContaSelecionada,
  };
}

type SelecionarFormaAcumulacaoAction = {
  type: "SELECIONAR_FORMA_ACUMULACAO",
  formaAcumulacaoSelecionada: Id,
};

export function selecionarFormaAcumulacao(
  formaAcumulacaoSelecionada: Id
): SelecionarFormaAcumulacaoAction {
  return {
    type: "SELECIONAR_FORMA_ACUMULACAO",
    formaAcumulacaoSelecionada,
  };
}

type SelecionarCapitalUtilizadoAction = {
  type: "SELECIONAR_CAPITAL_UTILIZADO",
  capitalUtilizadoSelecionado: Id,
};

export function selecionarCapitalutilizado(
  capitalUtilizadoSelecionado: Id
): SelecionarCapitalUtilizadoAction {
  return {
    type: "SELECIONAR_CAPITAL_UTILIZADO",
    capitalUtilizadoSelecionado,
  };
}

type AdicionarMedidasComDescricaoAction = {
  type: "ADICIONAR_MEDIDA_COM_DESCRICAO",
  medidasSelecionadas: Array<MedidaSelecionada>,
  posicao: number,
  novaMedida: boolean,
};

export function adicionarMedidasComDescricao(
  medidasSelecionadas: Array<MedidaSelecionada>,
  posicao: number = -1,
  novaMedida: boolean = false
): AdicionarMedidasComDescricaoAction {
  return {
    type: "ADICIONAR_MEDIDA_COM_DESCRICAO",
    medidasSelecionadas,
    posicao,
    novaMedida,
  };
}

type RestaurarConfiguracoesRelatorioAction = {
  type: "RESTAURAR_CONFIGURACOES_RELATORIO",
  medidas: Array<MedidaSelecionada>,
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio,
};
export function restaurarConfiguracoesRelatorio(
  medidas: Array<MedidaSelecionada>,
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio
): RestaurarConfiguracoesRelatorioAction {
  return {
    type: "RESTAURAR_CONFIGURACOES_RELATORIO",
    medidas,
    configuracaoGlobalRelatorio,
  };
}

type SetConfiguracoesRelatorioSalvoAction = {
  type: "SET_CONFIGURACOES_RELATORIO_SALVO",
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio,
};
export function setConfiguracoesRelatorioSalvo(
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio
): SetConfiguracoesRelatorioSalvoAction {
  return {
    type: "SET_CONFIGURACOES_RELATORIO_SALVO",
    configuracaoGlobalRelatorio,
  };
}

type ConfiguracoesRestauradasAction = {
  type: "CONFIGURACOES_RESTAURADAS",
};
export const configuracoesRestauradas = (): ConfiguracoesRestauradasAction => ({
  type: "CONFIGURACOES_RESTAURADAS",
});

type AbrirExplicacaoQtLinkAction = {
  type: "ABRIR_EXPLICACAO_QT_LINK",
};

export const abrirExplicacaoQtLink = (): AbrirExplicacaoQtLinkAction => ({
  type: "ABRIR_EXPLICACAO_QT_LINK",
});

type ExportarFuncaoQtlink = {
  type: "EXPORTAR_FUNCAO_QTLINK",
};

export function exportarFuncaoQtlink(): ExportarFuncaoQtlink {
  return {
    type: "EXPORTAR_FUNCAO_QTLINK",
  };
}

type ExportarFuncaoQtlinkSuccessAction = {
  type: "EXPORTAR_FUNCAO_QTLINK_SUCCESS",
  funcaoQtLink: string,
};

export function exportarFuncaoQtlinkSucess(
  funcaoQtLink: string
): ExportarFuncaoQtlinkSuccessAction {
  return {
    type: "EXPORTAR_FUNCAO_QTLINK_SUCCESS",
    funcaoQtLink,
  };
}

type ExportarFuncaoQtlinkErroAction = {
  type: "EXPORTAR_FUNCAO_QTLINK_ERRO",
};

export function falhaAoExpotarFuncaoQtlink(): ExportarFuncaoQtlinkErroAction {
  return {
    type: "EXPORTAR_FUNCAO_QTLINK_ERRO",
  };
}

type SelecionarToleranciaDFAction = {
  type: "SELECIONAR_TOLERANCIA_DF",
  toleranciaDF: Id,
};

export function selecionarToleranciaDF(
  toleranciaDF: Id
): SelecionarToleranciaDFAction {
  return {
    type: "SELECIONAR_TOLERANCIA_DF",
    toleranciaDF,
  };
}

type SelecionarAnosAction = {
  type: "SELECIONAR_ANOS",
  anos: Array<Id>,
};

export function selecionarAnos(anos: Array<Id>): SelecionarAnosAction {
  return {
    type: "SELECIONAR_ANOS",
    anos,
  };
}

type SelecionarTipoReferenciaAction = {
  type: "SELECIONAR_TIPO_REFERENCIA",
  tipoReferencia: Id,
};

export function selecionarTipoReferencia(
  tipoReferencia: Id
): SelecionarTipoReferenciaAction {
  return {
    type: "SELECIONAR_TIPO_REFERENCIA",
    tipoReferencia,
  };
}

type SelecionarTrimestreReferenciaAction = {
  type: "SELECIONAR_TRIMESTRE_REFERENCIA",
  trimestreReferencia: Id,
};

export function selecionarTrimestreReferencia(
  trimestreReferencia: Id
): SelecionarTrimestreReferenciaAction {
  return {
    type: "SELECIONAR_TRIMESTRE_REFERENCIA",
    trimestreReferencia,
  };
}

type SelecionarAnoReferenciaAction = {
  type: "SELECIONAR_ANO_REFERENCIA",
  anoReferencia: Id,
};

export function selecionarAnoReferencia(
  anoReferencia: Id
): SelecionarAnoReferenciaAction {
  return {
    type: "SELECIONAR_ANO_REFERENCIA",
    anoReferencia,
  };
}

type SelecionarComparacaoAction = {
  type: "SELECIONAR_COMPARACAO",
  comparacao: Id,
};

export function selecionarComparacao(
  comparacao: Id
): SelecionarComparacaoAction {
  return {
    type: "SELECIONAR_COMPARACAO",
    comparacao,
  };
}

type EditarLambdaAction = {
  type: "EDITAR_LAMBDA",
  lambda: number,
};

export function editarLambda(lambda: number): EditarLambdaAction {
  return { type: "EDITAR_LAMBDA", lambda };
}

type EditarLimiteAction = {
  type: "EDITAR_LIMITE",
  limite: number,
};

export function editarLimite(limite: number): EditarLimiteAction {
  return { type: "EDITAR_LIMITE", limite };
}

type SelecionarAtivoSemRiscoAction = {
  type: "SELECIONAR_ATIVO_SEM_RISCO",
  ativoSemRiscoSelecionado: string,
};

export function selecionarAtivoSemRisco(
  ativoSemRiscoSelecionado: string
): SelecionarAtivoSemRiscoAction {
  return {
    type: "SELECIONAR_ATIVO_SEM_RISCO",
    ativoSemRiscoSelecionado,
  };
}

type SelecionarPrimeiroAtivoSemRiscoAction = {
  type: "SELECIONAR_PRIMEIRO_ATIVO_SEM_RISCO",
};

export function selecionarPrimeiroAtivoSemRisco(): SelecionarPrimeiroAtivoSemRiscoAction {
  return {
    type: "SELECIONAR_PRIMEIRO_ATIVO_SEM_RISCO",
  };
}

type SelecionarTaxaDeAdministracaoAction = {
  type: "SELECIONAR_TAXA_DE_ADMINISTRACAO",
  taxaDeAdministracaoSelecionada: Id,
};

export function selecionarTaxaDeAdministracao(
  taxaDeAdministracaoSelecionada: Id
): SelecionarTaxaDeAdministracaoAction {
  return {
    type: "SELECIONAR_TAXA_DE_ADMINISTRACAO",
    taxaDeAdministracaoSelecionada,
  };
}

type EditarHorizonteDuAction = {
  type: "EDITAR_HORIZONTE_DU",
  horizonte: number,
};

export function editarHorizonteDu(horizonte: number): EditarHorizonteDuAction {
  return {
    type: "EDITAR_HORIZONTE_DU",
    horizonte,
  };
}

type EditarConfiancaAction = {
  type: "EDITAR_CONFIANCA",
  confianca: number,
};

export function editarConfianca(confianca: number): EditarConfiancaAction {
  return {
    type: "EDITAR_CONFIANCA",
    confianca,
  };
}

type SelecionarVolatilidadeAction = {
  type: "SELECIONAR_VOLATILIDADE",
  volatilidadeSelecionada: Id,
};

export function selecionarVolatilidade(
  volatilidadeSelecionada: Id
): SelecionarVolatilidadeAction {
  return {
    type: "SELECIONAR_VOLATILIDADE",
    volatilidadeSelecionada,
  };
}

type SelecionarTipoDoRetornoAction = {
  type: "SELECIONAR_TIPO_DO_RETORNO",
  tipoDoRetornoSelecionado: Id,
};

export function selecionarTipoDoRetorno(
  tipoDoRetornoSelecionado: Id
): SelecionarTipoDoRetornoAction {
  return {
    type: "SELECIONAR_TIPO_DO_RETORNO",
    tipoDoRetornoSelecionado,
  };
}

type SelecionarBenchmarkAction = {
  type: "SELECIONAR_BENCHMARK",
  benchmarkSelecionado: string,
};

export function selecionarBenchmark(
  benchmarkSelecionado: string
): SelecionarBenchmarkAction {
  return {
    type: "SELECIONAR_BENCHMARK",
    benchmarkSelecionado,
  };
}

type SelecionarPrimeiroBenchmarkAction = {
  type: "SELECIONAR_PRIMEIRO_BENCHMARK",
};

export function selecionarPrimeiroBenchmark(): SelecionarPrimeiroBenchmarkAction {
  return {
    type: "SELECIONAR_PRIMEIRO_BENCHMARK",
  };
}

type SelecionarCompetenciaAction = {
  type: "SELECIONAR_COMPETENCIA",
  competencia: string,
};

export function selecionarCompetencia(
  competencia: string
): SelecionarCompetenciaAction {
  return {
    type: "SELECIONAR_COMPETENCIA",
    competencia,
  };
}

type SelecionarPrimeiraCompetenciaAction = {
  type: "SELECIONAR_PRIMEIRA_COMPETENCIA",
};

export function selecionarPrimeiraCompetencia(): SelecionarPrimeiraCompetenciaAction {
  return {
    type: "SELECIONAR_PRIMEIRA_COMPETENCIA",
  };
}

type SelecionarIndiceAction = {
  type: "SELECIONAR_INDICE",
  indice: string,
};

export function selecionarIndice(indice: string): SelecionarIndiceAction {
  return {
    type: "SELECIONAR_INDICE",
    indice,
  };
}

type SelecionarIndicesAction = {
  type: "SELECIONAR_INDICES",
  indices: Array<string>,
};

export function selecionarIndices(
  indices: Array<string>
): SelecionarIndicesAction {
  return {
    type: "SELECIONAR_INDICES",
    indices,
  };
}

type SelecionarPrimeiroIndiceAction = {
  type: "SELECIONAR_PRIMEIRO_INDICE",
};

export function selecionarPrimeiroIndice(): SelecionarPrimeiroIndiceAction {
  return {
    type: "SELECIONAR_PRIMEIRO_INDICE",
  };
}

type SelecionarFrequenciasAction = {
  type: "SELECIONAR_FREQUENCIAS",
  frequencias: Array<Id>,
};

export function selecionarFrequencias(
  frequencias: Array<Id>
): SelecionarFrequenciasAction {
  return {
    type: "SELECIONAR_FREQUENCIAS",
    frequencias,
  };
}

type SelecionarAjustesPrecoAction = {
  type: "SELECIONAR_AJUSTES_PRECO",
  ajustesPreco: Array<Id>,
};

export function selecionarAjustesPreco(
  ajustesPreco: Array<Id>
): SelecionarAjustesPrecoAction {
  return {
    type: "SELECIONAR_AJUSTES_PRECO",
    ajustesPreco,
  };
}

type SelecionarAjustesPrecoSemProventosAction = {
  type: "SELECIONAR_AJUSTES_PRECO_SEM_PROVENTOS",
  ajustesPrecoSemProventos: Array<Id>,
};

export function selecionarAjustesPrecoSemProventos(
  ajustesPrecoSemProventos: Array<Id>
): SelecionarAjustesPrecoSemProventosAction {
  return {
    type: "SELECIONAR_AJUSTES_PRECO_SEM_PROVENTOS",
    ajustesPrecoSemProventos,
  };
}

type SelecionarEscalaMoedaAction = {
  type: "SELECIONAR_ESCALA_MOEDA",
  escalaMoeda: Id,
};

export function selecionarEscalaMoeda(
  escalaMoeda: Id
): SelecionarEscalaMoedaAction {
  return {
    type: "SELECIONAR_ESCALA_MOEDA",
    escalaMoeda,
  };
}

type SelecionarPlUtilizadoAction = {
  type: "SELECIONAR_PL_UTILIZADO",
  plUtilizado: string,
};

export function selecionarPlUtilizado(
  plUtilizado: string
): SelecionarPlUtilizadoAction {
  return {
    type: "SELECIONAR_PL_UTILIZADO",
    plUtilizado,
  };
}

type SelecionarToleranciaDiasValorMercadoAction = {
  type: "SELECIONAR_TOLERANCIA_DIAS_VALOR_MERCADO",
  toleranciaDiasValorMercadoSelecionada: number,
};

export function selecionarToleranciaDiasValorMercado(
  toleranciaDiasValorMercadoSelecionada: number
): SelecionarToleranciaDiasValorMercadoAction {
  return {
    type: "SELECIONAR_TOLERANCIA_DIAS_VALOR_MERCADO",
    toleranciaDiasValorMercadoSelecionada,
  };
}

type SelecionarCorretorMonetarioAction = {
  type: "SELECIONAR_CORRETOR_MONETARIO",
  corretorMonetarioSelecionado: number,
};

export function selecionarCorretorMonetario(
  corretorMonetarioSelecionado: number
): SelecionarCorretorMonetarioAction {
  return {
    type: "SELECIONAR_CORRETOR_MONETARIO",
    corretorMonetarioSelecionado,
  };
}

type SelecionarDeflatorAction = {
  type: "SELECIONAR_DEFLATOR",
  deflatorSelecionado: number,
};

export function selecionarDeflator(
  deflatorSelecionado: number
): SelecionarDeflatorAction {
  return {
    type: "SELECIONAR_DEFLATOR",
    deflatorSelecionado,
  };
}

type SelecionarValorBaseDeflatorAction = {
  type: "SELECIONAR_VALOR_BASE_DEFLATOR",
  valorBaseDeflatorSelecionado: Id,
};

export function selecionarValorBaseDeflator(
  valorBaseDeflatorSelecionado: Id
): SelecionarValorBaseDeflatorAction {
  return {
    type: "SELECIONAR_VALOR_BASE_DEFLATOR",
    valorBaseDeflatorSelecionado,
  };
}

type SelecionarValorBaseDeflatorOutroAction = {
  type: "SELECIONAR_VALOR_BASE_DEFLATOR_OUTRO",
  valorBaseDeflatorOutroSelecionado: number,
};

export function selecionarValorBaseDeflatorOutro(
  valorBaseDeflatorOutroSelecionado: number
): SelecionarValorBaseDeflatorOutroAction {
  return {
    type: "SELECIONAR_VALOR_BASE_DEFLATOR_OUTRO",
    valorBaseDeflatorOutroSelecionado,
  };
}

type SelecionarDataBaseDeflatorAction = {
  type: "SELECIONAR_DATA_BASE_DEFLATOR",
  dataBaseDeflatorSelecionado: Id,
};

export function selecionarDataBaseDeflator(
  dataBaseDeflatorSelecionado: Id
): SelecionarDataBaseDeflatorAction {
  return {
    type: "SELECIONAR_DATA_BASE_DEFLATOR",
    dataBaseDeflatorSelecionado,
  };
}

type SelecionarDataBaseDeflatorDataEspecificaAction = {
  type: "SELECIONAR_DATA_BASE_DEFLATOR_DATA_ESPECIFICA",
  dataBaseDeflatorDataEspecificaSelecionada: Moment,
};

export function selecionarDataBaseDeflatorDataEspecifica(
  dataBaseDeflatorDataEspecificaSelecionada: Moment
): SelecionarDataBaseDeflatorDataEspecificaAction {
  return {
    type: "SELECIONAR_DATA_BASE_DEFLATOR_DATA_ESPECIFICA",
    dataBaseDeflatorDataEspecificaSelecionada,
  };
}

type SelecionarDataBaseAction = {
  type: "SELECIONAR_DATA_BASE",
  dataBaseSelecionada: Date,
};

export function selecionarDataBase(
  dataBaseSelecionada: Date
): SelecionarDataBaseAction {
  return {
    type: "SELECIONAR_DATA_BASE",
    dataBaseSelecionada,
  };
}

type SelecionarDataBaseDefaultAction = {
  type: "SELECIONAR_DATA_BASE_DEFAULT",
};

export function selecionarDataBaseDefault(): SelecionarDataBaseDefaultAction {
  return {
    type: "SELECIONAR_DATA_BASE_DEFAULT",
  };
}

type SelecionarDataEspecificaAction = {
  type: "SELECIONAR_DATA_ESPECIFICA",
  dataEspecificaSelecionada: Date,
};

export function selecionarDataEspecifica(
  dataEspecificaSelecionada: Date
): SelecionarDataEspecificaAction {
  return {
    type: "SELECIONAR_DATA_ESPECIFICA",
    dataEspecificaSelecionada,
  };
}

type SelecionarDataEspecificaDefaultAction = {
  type: "SELECIONAR_DATA_ESPECIFICA_DEFAULT",
};

export function selecionarDataEspecificaDefault(): SelecionarDataEspecificaDefaultAction {
  return {
    type: "SELECIONAR_DATA_ESPECIFICA_DEFAULT",
  };
}

type SelecionarDataInicialCompostaAction = {
  type: "SELECIONAR_DATA_INICIAL_COMPOSTA",
  dataInicialCompostaSelecionada: string,
};

export function selecionarDataInicialComposta(
  dataInicialCompostaSelecionada: string
): SelecionarDataInicialCompostaAction {
  return {
    type: "SELECIONAR_DATA_INICIAL_COMPOSTA",
    dataInicialCompostaSelecionada,
  };
}

type SelecionarPrimeiraDataInicialCompostaAction = {
  type: "SELECIONAR_PRIMEIRA_DATA_INICIAL_COMPOSTA",
};

export function selecionarPrimeiraDataInicialComposta(): SelecionarPrimeiraDataInicialCompostaAction {
  return {
    type: "SELECIONAR_PRIMEIRA_DATA_INICIAL_COMPOSTA",
  };
}

type SelecionarDataFinalCompostaAction = {
  type: "SELECIONAR_DATA_FINAL_COMPOSTA",
  dataFinalCompostaSelecionada: string,
};

export function selecionarDataFinalComposta(
  dataFinalCompostaSelecionada: string
): SelecionarDataFinalCompostaAction {
  return {
    type: "SELECIONAR_DATA_FINAL_COMPOSTA",
    dataFinalCompostaSelecionada,
  };
}

type SelecionarPrimeiraDataFinalCompostaAction = {
  type: "SELECIONAR_PRIMEIRA_DATA_FINAL_COMPOSTA",
};

export function selecionarPrimeiraDataFinalComposta(): SelecionarPrimeiraDataFinalCompostaAction {
  return {
    type: "SELECIONAR_PRIMEIRA_DATA_FINAL_COMPOSTA",
  };
}

type SelecionarDataInicialAction = {
  type: "SELECIONAR_DATA_INICIAL",
  dataInicialSelecionada: Date,
};

export function selecionarDataInicial(
  dataInicialSelecionada: Date
): SelecionarDataInicialAction {
  return {
    type: "SELECIONAR_DATA_INICIAL",
    dataInicialSelecionada,
  };
}

type SelecionarDataFinalAction = {
  type: "SELECIONAR_DATA_FINAL",
  dataFinalSelecionada: Date,
};

export function selecionarDataFinal(
  dataFinalSelecionada: Date
): SelecionarDataFinalAction {
  return {
    type: "SELECIONAR_DATA_FINAL",
    dataFinalSelecionada,
  };
}

type ObterDatasUteisSelecaoJanelaAction = {
  type: "OBTER_DATAS_UTEIS_SELECAO_JANELA",
};

export function obterDatasUteisSelecaoJanela(): ObterDatasUteisSelecaoJanelaAction {
  return {
    type: "OBTER_DATAS_UTEIS_SELECAO_JANELA",
  };
}

type ObterDatasUteisSelecaoJanelaSuccessAction = {
  type: "OBTER_DATAS_UTEIS_SELECAO_JANELA_SUCCESS",
  datasUteisSelecaoJanela: Array<DatasSelecaoJanela>,
};

export function obterDatasUteisSelecaoJanelaSucess(
  datasUteisSelecaoJanela: Array<DatasSelecaoJanela>
): ObterDatasUteisSelecaoJanelaSuccessAction {
  return {
    type: "OBTER_DATAS_UTEIS_SELECAO_JANELA_SUCCESS",
    datasUteisSelecaoJanela,
  };
}

type ObterMesesSelecaoJanelaAction = {
  type: "OBTER_MESES_SELECAO_JANELA",
};

export function obterMesesSelecaoJanela(): ObterMesesSelecaoJanelaAction {
  return {
    type: "OBTER_MESES_SELECAO_JANELA",
  };
}

type ObterMesesSelecaoJanelaSuccessAction = {
  type: "OBTER_MESES_SELECAO_JANELA_SUCCESS",
  mesesSelecaoJanela: Array<DatasSelecaoJanela>,
};

export function obterMesesSelecaoJanelaSucess(
  mesesSelecaoJanela: Array<DatasSelecaoJanela>
): ObterMesesSelecaoJanelaSuccessAction {
  return {
    type: "OBTER_MESES_SELECAO_JANELA_SUCCESS",
    mesesSelecaoJanela,
  };
}

type SelecionarTamanhoDaJanelaAction = {
  type: "SELECIONAR_TAMANHO_DA_JANELA",
  tamanhoJanelaSelecionada: number,
};

export function selecionarTamanhoJanela(
  tamanhoJanelaSelecionada: number
): SelecionarTamanhoDaJanelaAction {
  return { type: "SELECIONAR_TAMANHO_DA_JANELA", tamanhoJanelaSelecionada };
}

type SelecionarQuantidadeJanelasAction = {
  type: "SELECIONAR_QUANTIDADE_JANELAS",
  quantidadeJanelasSelecionada: number,
};

export function selecionarQuantidadeJanelas(
  quantidadeJanelasSelecionada: number
): SelecionarQuantidadeJanelasAction {
  return {
    type: "SELECIONAR_QUANTIDADE_JANELAS",
    quantidadeJanelasSelecionada,
  };
}

type SelecionarTipoJanelaAction = {
  type: "SELECIONAR_TIPO_JANELA",
  tipoJanelaSelecionada: Id,
};

export function selecionarTipoJanela(
  tipoJanelaSelecionada: Id
): SelecionarTipoJanelaAction {
  return { type: "SELECIONAR_TIPO_JANELA", tipoJanelaSelecionada };
}

type SelecionarFiltrosTipoAtivoAction = {
  type: "SELECIONAR_FILTROS_TIPO_ATIVO",
  filtrosSelecionados: Array<Id>,
};

export function selecionarFiltrosTipoAtivo(
  filtrosSelecionados: Array<Id>
): SelecionarFiltrosTipoAtivoAction {
  return {
    type: "SELECIONAR_FILTROS_TIPO_ATIVO",
    filtrosSelecionados,
  };
}

type DeselecionarFiltrosTipoAtivoAction = {
  type: "DESELECIONAR_FILTROS_TIPO_ATIVO",
  filtrosDeselecionados: Array<Id>,
};

export function deselecionarFiltrosTipoAtivo(
  filtrosDeselecionados: Array<Id>
): DeselecionarFiltrosTipoAtivoAction {
  return {
    type: "DESELECIONAR_FILTROS_TIPO_ATIVO",
    filtrosDeselecionados,
  };
}

type SelecionarGrupamentoMedidaAction = {
  type: "SELECIONAR_GRUPAMENTO_MEDIDA",
  grupamento: GrupamentosMedida,
  suprimirFlagAlteracao?: boolean,
};

export function selecionarGrupamentoMedida(
  grupamento: GrupamentosMedida,
  suprimirFlagAlteracao: boolean = false
): SelecionarGrupamentoMedidaAction {
  return {
    type: "SELECIONAR_GRUPAMENTO_MEDIDA",
    grupamento,
    suprimirFlagAlteracao: suprimirFlagAlteracao || false,
  };
}

type TrocarMedidaDePosicaoAction = {
  type: "TROCAR_MEDIDA_DE_POSICAO",
  indiceOrigem: number,
  indiceDestino: number,
};

export function trocarMedidaDePosicao(
  indiceOrigem: number,
  indiceDestino: number
): TrocarMedidaDePosicaoAction {
  return {
    type: "TROCAR_MEDIDA_DE_POSICAO",
    indiceOrigem,
    indiceDestino,
  };
}

type MarcarMedidaSelecionadaAction = {
  type: "MARCAR_MEDIDA_SELECIONADA",
  indiceMedida: number,
};

export function restaurarNomePadraoDeMedidaSelecionada(
  indiceMedida: number
): RestaurarNomePadraoDeMedidaSelecionadaAction {
  return {
    type: "RESTAURAR_NOME_PADRAO_DE_MEDIDA_SELECIONADA",
    indiceMedida,
  };
}

type RestaurarNomePadraoDeMedidaSelecionadaAction = {
  type: "RESTAURAR_NOME_PADRAO_DE_MEDIDA_SELECIONADA",
  indiceMedida: number,
};

export function renomearMedidaSelecionada(
  indiceMedida: number,
  label: string
): RenomearMedidaSelecionadaAction {
  return {
    type: "RENOMEAR_MEDIDA_SELECIONADA",
    indiceMedida,
    label,
  };
}

type RenomearMedidaSelecionadaAction = {
  type: "RENOMEAR_MEDIDA_SELECIONADA",
  indiceMedida: number,
  label: string,
};

export function marcarMedidaSelecionada(
  indiceMedida: number
): MarcarMedidaSelecionadaAction {
  return {
    type: "MARCAR_MEDIDA_SELECIONADA",
    indiceMedida,
  };
}

type DesmarcarMedidaSelecionadaAction = {
  type: "DESMARCAR_MEDIDA_SELECIONADA",
  indiceMedida: number,
};

export function desmarcarMedidaSelecionada(
  indiceMedida: number
): DesmarcarMedidaSelecionadaAction {
  return {
    type: "DESMARCAR_MEDIDA_SELECIONADA",
    indiceMedida,
  };
}

type MarcarTodasMedidasSelecionadasAction = {
  type: "MARCAR_TODAS_MEDIDAS_SELECIONADAS",
};

export function marcarTodasMedidasSelecionada(): MarcarTodasMedidasSelecionadasAction {
  return {
    type: "MARCAR_TODAS_MEDIDAS_SELECIONADAS",
  };
}

type DesmarcarTodasMedidaSelecionadasAction = {
  type: "DESMARCAR_TODAS_MEDIDAS_SELECIONADAS",
};

export function desmarcarTodasMedidasSelecionada(): DesmarcarTodasMedidaSelecionadasAction {
  return {
    type: "DESMARCAR_TODAS_MEDIDAS_SELECIONADAS",
  };
}

type ExcluirMedidasMarcadasAction = {
  type: "EXCLUIR_MEDIDAS_MARCADAS",
  remocaoLogica: boolean,
};

export function excluirMedidasMarcadas(): ExcluirMedidasMarcadasAction {
  return {
    type: "EXCLUIR_MEDIDAS_MARCADAS",
    remocaoLogica: true,
  };
}

type EditarMedidaAction = {
  type: "EDITAR_MEDIDA_SELECIONADA",
  indiceMedida: number,
};

export function editarMedidaSelecionada(
  indiceMedida: number
): EditarMedidaAction {
  return {
    type: "EDITAR_MEDIDA_SELECIONADA",
    indiceMedida,
  };
}

type ExcluirMedidaAction = {
  type: "EXCLUIR_MEDIDA_SELECIONADA",
  indiceMedida: number,
  remocaoLogica: boolean,
};

export function excluirMedidaSelecionada(
  indiceMedida: number
): ExcluirMedidaAction {
  return {
    type: "EXCLUIR_MEDIDA_SELECIONADA",
    indiceMedida,
    remocaoLogica: true,
  };
}

type SelecionarValorComboMedidasAction = {
  type: "SELECIONAR_VALOR_COMBO_MEDIDAS",
  valor: string,
};

export function selecionarValorComboMedidas(
  valor: string
): SelecionarValorComboMedidasAction {
  return {
    type: "SELECIONAR_VALOR_COMBO_MEDIDAS",
    valor,
  };
}
type GerarRelatorioAction = {
  type: "GERAR_RELATORIO",
};

export function gerarRelatorio(): GerarRelatorioAction {
  return { type: "GERAR_RELATORIO" };
}

type RelatorioGeradoAction = {
  type: "RELATORIO_GERADO",
};

export function relatorioGerado(): RelatorioGeradoAction {
  return { type: "RELATORIO_GERADO" };
}

type SelecionarAtivosAction = {
  type: "SELECIONAR_ATIVOS",
  ativos: Array<AtivoSelecionado>,
};

export function selecionarAtivos(
  ativos: Array<AtivoSelecionado>
): SelecionarAtivosAction {
  return { type: "SELECIONAR_ATIVOS", ativos };
}

type CompartilharArquivoAction = {
  type: "COMPARTILHAR_ARQUIVO",
  payload: {
    arquivo: ArquivoMedida,
    nomeArquivoCompartilhado: string,
    codigosUsuariosACompartilhar: Array<Id>,
  },
};

export const compartilharArquivo = (payload: *): CompartilharArquivoAction => ({
  type: "COMPARTILHAR_ARQUIVO",
  payload,
});

type AbrirPaginaArquivosAction = {
  type: "ABRIR_PAGINA_ARQUIVOS",
  operacao: OperacaoArquivos,
  extraInfo?: any,
};

export const abrirPaginaArquivos = (
  operacao: OperacaoArquivos,
  extraInfo: *
): AbrirPaginaArquivosAction => ({
  type: "ABRIR_PAGINA_ARQUIVOS",
  operacao,
  extraInfo,
});

type AbrirArquivoAction = {
  type: "ABRIR_ARQUIVO",
  arquivo: string | ArquivoMedida,
  sobrescreverMedidas: boolean,
  gerarRelatorio: boolean,
};

type OpcoesAbrirArquivos = {
  sobrescreverMedidas?: boolean,
  gerarRelatorio?: boolean,
};
export function abrirArquivo(
  arquivo: string | ArquivoMedida,
  {
    sobrescreverMedidas = true,
    gerarRelatorio = false,
  }: OpcoesAbrirArquivos = {}
): AbrirArquivoAction {
  return {
    type: "ABRIR_ARQUIVO",
    arquivo,
    sobrescreverMedidas,
    gerarRelatorio,
  };
}

type AbrirArquivoSuccessAction = {
  type: "ABRIR_ARQUIVO_SUCCESS",
};
export function abrirArquivoSuccess(): AbrirArquivoSuccessAction {
  return { type: "ABRIR_ARQUIVO_SUCCESS" };
}

type AberturaDeArquivoCanceladaAction = {
  type: "ABERTURA_DE_ARQUIVO_CANCELADA",
};
export function cancelaAberturaDeArquivo(): AberturaDeArquivoCanceladaAction {
  return { type: "ABERTURA_DE_ARQUIVO_CANCELADA" };
}

type SalvarArquivoAction = {
  type: "SALVAR_ARQUIVO",
  arquivo: ArquivoMedida,
};

export function salvarArquivo(arquivo: ArquivoMedida): SalvarArquivoAction {
  return { type: "SALVAR_ARQUIVO", arquivo };
}

type ArquivoSalvoAction = {
  type: "ARQUIVO_SALVO",
  arquivo: ArquivoMedida,
};

export function arquivoSalvo(arquivo: ArquivoMedida): ArquivoSalvoAction {
  return { type: "ARQUIVO_SALVO", arquivo };
}

type ErroAoSalvarArquivoAction = {
  type: "ERRO_AO_SALVAR_ARQUIVO",
  arquivo: ArquivoMedida,
};

export function erroAoSalvarArquivo(
  arquivo: ArquivoMedida
): ErroAoSalvarArquivoAction {
  return { type: "ERRO_AO_SALVAR_ARQUIVO", arquivo };
}

type ErroAoAbrirArquivoAction = {
  type: "ERRO_AO_ABRIR_ARQUIVO",
  arquivo: ArquivoMedida,
};

export function erroAoAbrirArquivo(
  arquivo: ArquivoMedida
): ErroAoAbrirArquivoAction {
  return { type: "ERRO_AO_ABRIR_ARQUIVO", arquivo };
}

type AbrirTemplateAction = {
  type: "ABRIR_TEMPLATE",
  arquivo: ArquivoMedida,
};

export function abrirTemplate(arquivo: ArquivoMedida): AbrirTemplateAction {
  return { type: "ABRIR_TEMPLATE", arquivo };
}

type ExcluirArquivoAction = {
  type: "EXCLUIR_ARQUIVO",
  arquivo: ArquivoMedida,
};

export function excluirArquivo(arquivo: ArquivoMedida): ExcluirArquivoAction {
  return { type: "EXCLUIR_ARQUIVO", arquivo };
}

type LimparSelecaoAction = {
  type: "LIMPAR_SELECAO",
};

export function limparSelecao(): LimparSelecaoAction {
  return { type: "LIMPAR_SELECAO" };
}

type CarregarMedidasIniciaisSuccessAction = {
  type: "CARREGAR_MEDIDAS_INICIAIS_SUCCESS",
};

export function carregarMedidasIniciaisSuccess(): CarregarMedidasIniciaisSuccessAction {
  return { type: "CARREGAR_MEDIDAS_INICIAIS_SUCCESS" };
}

type DefineChavesI18nAction = {
  type: "DEFINE_CHAVES_I18N",
  chaves: Object,
};

export function defineChavesI18n(chaves: Object): DefineChavesI18nAction {
  return { type: "DEFINE_CHAVES_I18N", chaves };
}

type ExportarExcelVinculadoAction = {
  type: "EXPORTAR_EXCEL_VINCULADO",
};

export function exportarExcelVinculado(): ExportarExcelVinculadoAction {
  return { type: "EXPORTAR_EXCEL_VINCULADO" };
}

type ObterDetalhesUsuarioSuccessAction = {
  type: "OBTER_DETALHES_USUARIO_SUCCESS",
  detalhes: DetalhesUsuario,
};

export function obterDetalhesUsuarioSuccess(
  detalhes: DetalhesUsuario
): ObterDetalhesUsuarioSuccessAction {
  return { type: "OBTER_DETALHES_USUARIO_SUCCESS", detalhes };
}

type SelecionarTipoCalculoAction = {
  type: "SELECIONAR_TIPO_CALCULO",
  tipoCalculo: Id,
};

export function selecionarTipoCalculo(
  tipoCalculo: Id
): SelecionarTipoCalculoAction {
  return { type: "SELECIONAR_TIPO_CALCULO", tipoCalculo };
}

type SelecionarFechamentoDeMesAction = {
  type: "SELECIONAR_FECHAMENTO_DE_MES",
  checked: boolean,
};

export function selecionarFechamentoDeMes(
  checked: boolean
): SelecionarFechamentoDeMesAction {
  return { type: "SELECIONAR_FECHAMENTO_DE_MES", checked };
}

type SelecionaColunasRankingAction = {
  type: "SELECIONA_COLUNAS_RANKING",
  colunas: Array<Id>,
};

export const selecionaColunasRanking = (
  colunas: Array<Id>
): SelecionaColunasRankingAction => ({
  type: "SELECIONA_COLUNAS_RANKING",
  colunas,
});

type SelecionaPesoRankingAction = {
  type: "SELECIONA_PESO_RANKING",
  alias: string,
  peso: number,
};

export const selecionaPesoRanking = (
  alias: string,
  peso: number
): SelecionaPesoRankingAction => ({
  type: "SELECIONA_PESO_RANKING",
  alias,
  peso,
});

type SelecionaNomeMedidaCompostaAction = {
  type: "SELECIONA_NOME_MEDIDA_COMPOSTA",
  nome: string,
};

export const selecionaNomeMedidaComposta = (
  nome: string
): SelecionaNomeMedidaCompostaAction => ({
  type: "SELECIONA_NOME_MEDIDA_COMPOSTA",
  nome,
});

type SelecionaFormatoAction = {
  type: "SELECIONA_FORMATO_MEDIDA_COMPOSTA",
  formato: string,
};

export const selecionaFormatoMedidaComposta = (
  formato: string
): SelecionaFormatoAction => ({
  type: "SELECIONA_FORMATO_MEDIDA_COMPOSTA",
  formato,
});

type SelecionaExpressaoAction = {
  type: "SELECIONA_EXPRESSAO",
  expressao: string,
};

export const selecionaExpressao = (
  expressao: string
): SelecionaExpressaoAction => ({
  type: "SELECIONA_EXPRESSAO",
  expressao,
});

type SetMedidasSelecionadasDesabilitadoAction = {
  type: "SET_MEDIDAS_SELECIONADAS_DESABILITADO",
  desabilitado: boolean,
};

export const setMedidasSelecionadasDesabilitado = (
  desabilitado: boolean
): SetMedidasSelecionadasDesabilitadoAction => ({
  type: "SET_MEDIDAS_SELECIONADAS_DESABILITADO",
  desabilitado,
});

type SelecionarAtivosCarteiraAction = {
  type: "SELECIONAR_ATIVOS_CARTEIRA",
  ativos: Array<{ id: string, nome: string, tipoItemSelecionavel?: string }>,
};
export const selecionaAtivosCarteira = (
  ativos: Array<{ id: string, nome: string, tipoItemSelecionavel?: string }>
): SelecionarAtivosCarteiraAction => ({
  type: "SELECIONAR_ATIVOS_CARTEIRA",
  ativos,
});

type SelecionarCamposAction = {
  type: "SELECIONAR_CAMPOS",
  campos: Array<Id>,
};
export const selecionaCampos = (campos: Array<Id>): SelecionarCamposAction => ({
  type: "SELECIONAR_CAMPOS",
  campos,
});

type EditarCompradoresAction = {
  type: "EDITAR_COMPRADORES",
  compradores: number,
};
export const editarCompradores = (
  compradores: number
): EditarCompradoresAction => ({
  type: "EDITAR_COMPRADORES",
  compradores,
});

type SelecionarVisualizacaoCarteiraAction = {
  type: "SELECIONAR_VISUALIZACAO_CARTEIRA",
  visualizacaoCarteira: string,
};

export const selecionarVisualizacaoCarteira = (
  visualizacaoCarteira: string
): SelecionarVisualizacaoCarteiraAction => ({
  type: "SELECIONAR_VISUALIZACAO_CARTEIRA",
  visualizacaoCarteira,
});

type SelecionarRetornoNumeradorAction = {
  type: "SELECIONAR_RETORNO_NUMERADOR",
  retornoNumerador: string,
};

export const selecionarRetornoNumerador = (
  retornoNumerador: string
): SelecionarRetornoNumeradorAction => ({
  type: "SELECIONAR_RETORNO_NUMERADOR",
  retornoNumerador,
});

type SelecionarCalculoNumeradorAction = {
  type: "SELECIONAR_CALCULO_NUMERADOR",
  calculoNumerador: string,
};

export const selecionarCalculoNumerador = (
  calculoNumerador: string
): SelecionarCalculoNumeradorAction => ({
  type: "SELECIONAR_CALCULO_NUMERADOR",
  calculoNumerador,
});

type SelecionarPeriodicidadeEstatisticaNumeradorAction = {
  type: "SELECIONAR_PERIODICIDADE_ESTATISTICA_NUMERADOR",
  periodicidadeNumerador: string,
};

export const selecionarPeriodicidadeEstatisticaNumerador = (
  periodicidadeNumerador: string
): SelecionarPeriodicidadeEstatisticaNumeradorAction => ({
  type: "SELECIONAR_PERIODICIDADE_ESTATISTICA_NUMERADOR",
  periodicidadeNumerador,
});

type EditarDiasUteisPeriodicidadeNumeradorAction = {
  type: "EDITAR_DIAS_UTEIS_PERIODICIADE_NUMERADOR",
  diasUteisPeriodicidadeNumerador: number,
};

export function editarDiasUteisPeriodicidadeNumerador(
  diasUteisPeriodicidadeNumerador: number
): EditarDiasUteisPeriodicidadeNumeradorAction {
  return {
    type: "EDITAR_DIAS_UTEIS_PERIODICIADE_NUMERADOR",
    diasUteisPeriodicidadeNumerador,
  };
}

type SelecionarTipoVolatilidadeAction = {
  type: "SELECIONAR_TIPO_VOLATILIDADE",
  tipoVolatilidade: string,
};

export const selecionarTipoVolatilidade = (
  tipoVolatilidade: string
): SelecionarTipoVolatilidadeAction => ({
  type: "SELECIONAR_TIPO_VOLATILIDADE",
  tipoVolatilidade,
});

type SelecionarConfiguracaoSeries = {
  type: "SELECIONAR_CONFIGURACAO_SERIES",
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio,
};

export const selecionarConfiguracaoSeries = (
  configuracaoGlobalRelatorio: ConfiguracaoGlobalRelatorio
): SelecionarConfiguracaoSeries => ({
  type: "SELECIONAR_CONFIGURACAO_SERIES",
  configuracaoGlobalRelatorio,
});

type SelecionarEstimacaoVolatilidadeAction = {
  type: "SELECIONAR_ESTIMACAO_VOLATILIDADE",
  estimacaoVolatilidade: string,
};

export const selecionarEstimacaoVolatilidade = (
  estimacaoVolatilidade: string
): SelecionarEstimacaoVolatilidadeAction => ({
  type: "SELECIONAR_ESTIMACAO_VOLATILIDADE",
  estimacaoVolatilidade,
});

type SelecionarPeriodicidadeEstatisticaVolatilidadeAction = {
  type: "SELECIONAR_PERIODICIDADE_ESTATISTICA_VOLATILIDADE",
  periodicidadeVolatilidade: string,
};

export const selecionarPeriodicidadeEstatisticaVolatilidade = (
  periodicidadeVolatilidade: string
): SelecionarPeriodicidadeEstatisticaVolatilidadeAction => ({
  type: "SELECIONAR_PERIODICIDADE_ESTATISTICA_VOLATILIDADE",
  periodicidadeVolatilidade,
});

type EditarDiasUteisPeriodicidadeVolatilidadeAction = {
  type: "EDITAR_DIAS_UTEIS_PERIODICIADE_VOLATILIDADE",
  diasUteisPeriodicidadeVolatilidade: number,
};

export function editarDiasUteisPeriodicidadeVolatilidade(
  diasUteisPeriodicidadeVolatilidade: number
): EditarDiasUteisPeriodicidadeVolatilidadeAction {
  return {
    type: "EDITAR_DIAS_UTEIS_PERIODICIADE_VOLATILIDADE",
    diasUteisPeriodicidadeVolatilidade,
  };
}

type SelecionarTipoRetornoEstatisticaAction = {
  type: "SELECIONAR_TIPO_RETORNO_ESTATISTICA",
  tipoRetornoConfiguracoesEstatistica: string,
};

export const selecionarTipoRetornoEstatistica = (
  tipoRetornoConfiguracoesEstatistica: string
): SelecionarTipoRetornoEstatisticaAction => ({
  type: "SELECIONAR_TIPO_RETORNO_ESTATISTICA",
  tipoRetornoConfiguracoesEstatistica,
});

export type AssetsAddedAction = {
  type: "ASSETS_ADDED",
  payload: Array<AssetKey>,
};
export const assetsAdded = (assetKeys: *): AssetsAddedAction => ({
  type: "ASSETS_ADDED",
  payload: assetKeys,
});

export type AssetsRemovedAction = {
  type: "ASSETS_REMOVED",
  payload: Array<AssetKey>,
};
export const assetsRemoved = (assetKeys: *): AssetsRemovedAction => ({
  type: "ASSETS_REMOVED",
  payload: assetKeys,
});

export type ExportReportAction = {
  type: "EXPORT_REPORT",
  payload: ExportType,
  meta: ReportId,
};
export const exportReportAsExcel = (
  reportId: ReportId
): ExportReportAction => ({
  type: "EXPORT_REPORT",
  payload: "xlsx",
  meta: reportId,
});
export const exportReportAsLinkedExcel = (
  reportId: ReportId
): ExportReportAction => ({
  type: "EXPORT_REPORT",
  payload: "linked-xlsx",
  meta: reportId,
});
export const exportReportAsCSV = (reportId: ReportId): ExportReportAction => ({
  type: "EXPORT_REPORT",
  payload: "csv",
  meta: reportId,
});

export type ReportExportedAction = {
  type: "REPORT_EXPORTED",
  payload: ExportType,
};
export const reportExported = (type: ExportType): ReportExportedAction => ({
  type: "REPORT_EXPORTED",
  payload: type,
});

export type ReportExportingErrorAction = {
  type: "REPORT_EXPORTING_ERROR",
  payload: ExportType,
};
export const reportExportingError = (
  type: ExportType,
  error: Error
): ReportExportingErrorAction => ({
  type: "REPORT_EXPORTING_ERROR",
  payload: type,
  meta: error,
});

export type FetchReportAction = {
  type: "FETCH_REPORT",
  meta: { reportId: ReportId },
};
export const fetchReport = (reportId: ReportId): FetchReportAction => ({
  type: "FETCH_REPORT",
  meta: { reportId },
});

export type SetReportLoadingAction = {
  type: "SET_REPORT_LOADING",
  meta: { reportId: ReportId },
  payload: boolean,
};
export const setReportLoading = (reportId: ReportId, loading: boolean) => ({
  type: "SET_REPORT_LOADING",
  meta: { reportId },
  payload: loading,
});

export type OpenAssetMenuAction = {
  type: "OPEN_ASSET_MENU",
  payload: MouseEvent,
  meta: AssetKey,
};
export const openAssetMenu = (key: AssetKey, event: MouseEvent) => ({
  type: "OPEN_ASSET_MENU",
  meta: key,
  payload: event,
});

export type OpenNewTaskAction = {
  type: "OPEN_NEW_TASK",
};
export const openNewTask = (): OpenNewTaskAction => ({
  type: "OPEN_NEW_TASK",
});

export type ReportFetchedAction = {
  type: "REPORT_FETCHED",
  payload: Report,
  meta: { reportId: ReportId },
};
export const reportFetched: CurriedFn2<
  ReportId,
  Object,
  ReportFetchedAction
> = curry((reportId, report) => ({
  type: "REPORT_FETCHED",
  payload: report,
  meta: { reportId },
}));

export type AddReportRowsAction = {
  type: "ADD_REPORT_ROWS",
  payload: { rows: $PropertyType<Report, "rows">, metadata: Object },
  meta: { reportId: ReportId },
};
export const addReportRows: CurriedFn2<
  ReportId,
  $PropertyType<Report, "rows">,
  Object,
  AddReportRowsAction
> = curry((reportId, rows, metadata) => ({
  type: "ADD_REPORT_ROWS",
  payload: { rows, metadata },
  meta: { reportId },
}));

export type ReportFetchingErrorAction = {
  type: "REPORT_FETCHING_ERROR",
  error: Error,
  meta: { reportId: ReportId },
};
export const reportFetchingError: CurriedFn2<
  ReportId,
  Error,
  ReportFetchedAction
> = curry((reportId, error) => ({
  type: "REPORT_FETCHING_ERROR",
  error,
  meta: { reportId },
}));

export type RemoveAssetsAction = {
  type: "REMOVE_ASSETS",
  payload: Array<AssetKey>,
  meta: { reportId: ReportId },
};
export const removeAssets: CurriedFn2<
  ReportId,
  AssetKey,
  RemoveAssetsAction
> = curry((reportId, assetKeys) => ({
  type: "REMOVE_ASSETS",
  payload: assetKeys,
  meta: { reportId },
}));

export type OpenGlobalSearchAction = {
  type: "OPEN_GLOBAL_SEARCH",
  payload: string,
};
export const openGlobalSearch = (search: *): OpenGlobalSearchAction => ({
  type: "OPEN_GLOBAL_SEARCH",
  payload: search,
});

export type SaveAssetGroupAction = {
  type: "SAVE_ASSET_GROUP",
  payload: {
    group: Array<AssetKey>,
    name: string,
  },
  meta: boolean,
};
export const saveAssetGroup = ({
  group,
  name,
  override = false,
}: *): SaveAssetGroupAction => ({
  type: "SAVE_ASSET_GROUP",
  payload: {
    group,
    name,
  },
  meta: override,
});

export type SaveAssetGroupErrorAction = {
  type: "SAVE_ASSET_GROUP_ERROR",
  payload: Object,
};
export const saveAssetGroupError = (error: *): SaveAssetGroupErrorAction => ({
  type: "SAVE_ASSET_GROUP_ERROR",
  payload: error,
});

export type SaveAssetGroupSuccessAction = {
  type: "SAVE_ASSET_GROUP_SUCCESS",
};
export const saveAssetGroupSuccess = (): SaveAssetGroupSuccessAction => ({
  type: "SAVE_ASSET_GROUP_SUCCESS",
});

export type SendMailAction = {
  type: "SEND_MAIL",
  payload: {
    cco: string,
    body: string,
    subject: string,
    to: string,
  },
  meta: ReportId,
};
export const sendMail = (reportId: ReportId, mail: *): SendMailAction => ({
  type: "SEND_MAIL",
  payload: mail,
  meta: reportId,
});

export type SetCurrentUserAction = {
  type: "SET_CURRENT_USER",
  payload: CurrentUser,
};
export const setCurrentUser = (user: *): SetCurrentUserAction => ({
  type: "SET_CURRENT_USER",
  payload: user,
});

type DefinirMedidasFixas = {
  type: "DEFINIR_MEDIDAS_FIXAS",
  medidas: Array<Id>,
};
export const definirMedidasFixas = (medidas: *): DefinirMedidasFixas => ({
  type: "DEFINIR_MEDIDAS_FIXAS",
  medidas,
});

type DefinirMedidasSuportadasExcel = {
  type: "EXCEL_DEFINIR_MEDIDAS_SUPORTADAS",
  medidas: Array<Id>,
};
export const definirMedidasSuportadasExcel = (
  medidas: *
): DefinirMedidasSuportadasExcel => ({
  type: "EXCEL_DEFINIR_MEDIDAS_SUPORTADAS",
  medidas,
});

type DefinirJWTExcel = {
  type: "EXCEL_DEFINIR_JWT",
  jwt: string,
};
export const definirJWTExcel = (jwt: string): DefinirJWTExcel => ({
  type: "EXCEL_DEFINIR_JWT",
  jwt,
});

type EditarAtivoExcel = {
  type: "EXCEL_EDITAR_ATIVO_EXCEL",
  ativo: string,
};
export const editarAtivoExcel = (ativo: string): EditarAtivoExcel => ({
  type: "EXCEL_EDITAR_ATIVO_EXCEL",
  ativo,
});

type EditarModoBuscaDoAtivoExcel = {
  type: "EXCEL_EDITAR_MODO_ATIVO_BUSCA",
  modoDeBusca: ModoDeBuscaDeAtivoExcel,
};
export const editarModoBuscaDoAtivoExcel = (
  modoDeBusca: ModoDeBuscaDeAtivoExcel
): EditarModoBuscaDoAtivoExcel => ({
  type: "EXCEL_EDITAR_MODO_ATIVO_BUSCA",
  modoDeBusca,
});

type DefinirCompartilhamentoDeArquivos = {
  type: "DEFINIR_COMPARTILHAMENTO_DE_ARQUIVOS",
  permitido: boolean,
};
export const definirCompartilhamentoDeArquivo = (
  permitido: boolean
): DefinirCompartilhamentoDeArquivos => ({
  type: "DEFINIR_COMPARTILHAMENTO_DE_ARQUIVOS",
  permitido,
});

type SaveGridStateAction = {
  type: "SAVE_GRID_STATE",
  meta: GridStateSaveTypes,
  payload: Object,
};
export const saveGridState = (
  type: GridStateSaveTypes,
  state: Object
): SaveGridStateAction => ({
  type: "SAVE_GRID_STATE",
  meta: type,
  payload: state,
});

type RestoreGridStateAction = {
  type: "RESTORE_GRID_STATE",
  payload: GridState,
};
export const restoreGridState = (state: GridState): RestoreGridStateAction => ({
  type: "RESTORE_GRID_STATE",
  payload: state,
});

type ResetGridStateAction = {
  type: "RESET_GRID_STATE",
};
export const resetGridState = (): ResetGridStateAction => ({
  type: "RESET_GRID_STATE",
});

type PersistReportAction = {
  type: "PERSIST_REPORT",
  payload: ReportId,
};
export const persistReport = (reportId: ReportId): PersistReportAction => ({
  type: "PERSIST_REPORT",
  payload: reportId,
});

type SetIsAxisAction = {
  type: "SET_IS_AXIS",
  payload: boolean,
};
export const setIsAxis = (isAxis: boolean): SetIsAxisAction => ({
  type: "SET_IS_AXIS",
  payload: isAxis,
});

type OpenNewTabAction = {
  type: "OPEN_NEW_TAB",
};
export const openNewTab = (): OpenNewTabAction => ({
  type: "OPEN_NEW_TAB",
});

type OpenChartsTabAction = {
  type: "OPEN_CHARTS_TAB",
};
export const openChartsTab = (): OpenChartsTabAction => ({
  type: "OPEN_CHARTS_TAB",
});

type SelecionarCasasDecimaisMedidaAction = {
  type: "SELECIONAR_CASAS_DECIMAIS_MEDIDA",
  casasDecimais: number,
};
export const selecionarCasasDecimaisMedida = (
  casasDecimais: number
): SelecionarCasasDecimaisMedidaAction => ({
  type: "SELECIONAR_CASAS_DECIMAIS_MEDIDA",
  casasDecimais,
});

type SelecionarModoDeVisualizacaoSeriesAction = {
  type: "SELECIONAR_MODO_VISUALIZACAO_SERIES",
  modoVisualizacao: number,
};
export const selecionarModoDeVisualizacaoSeries = (
  modoVisualizacao: number
): SelecionarModoDeVisualizacaoSeriesAction => ({
  type: "SELECIONAR_MODO_VISUALIZACAO_SERIES",
  modoVisualizacao,
});

type SelecionarExibirDatasSemNegociacaoSeriesAction = {
  type: "SELECIONAR_MODO_EXIBIR_DATAS_SEM_NEGOCIACAO_SERIES",
  exibir: boolean,
};
export const selecionarExibirDatasSemNegociacaoSeries = (
  exibir: *
): SelecionarExibirDatasSemNegociacaoSeriesAction => ({
  type: "SELECIONAR_MODO_EXIBIR_DATAS_SEM_NEGOCIACAO_SERIES",
  exibir,
});

type SelecionarIdentificadorSeriesAction = {
  type: "SELECIONAR_IDENTIFICADOR_SERIES",
  identificador: Id,
};
export const selecionarIdentificadorSeries = (
  identificador: *
): SelecionarIdentificadorSeriesAction => ({
  type: "SELECIONAR_IDENTIFICADOR_SERIES",
  identificador,
});

type DeselecionarIdentificadorSeriesAction = {
  type: "DESELECIONAR_IDENTIFICADOR_SERIES",
  identificador: Id,
};
export const deselecionarIdentificadorSeries = (
  identificador: *
): DeselecionarIdentificadorSeriesAction => ({
  type: "DESELECIONAR_IDENTIFICADOR_SERIES",
  identificador,
});

export type Action =
  | EditarModoBuscaDoAtivoExcel
  | EditarAtivoExcel
  | DefinirJWTExcel
  | DefinirMedidasSuportadasExcel
  | DefinirCompartilhamentoDeArquivos
  | DefinirMedidasFixas
  | DefinirModoAppAction
  | SelecionarMedidaAction
  | SelecionarMedidaUsuarioAction
  | AdicionarMedidaParaPersonalizar
  | AdicionarMedidasParaPersonalizar
  | RemoverMedidaParaPersonalizar
  | GerarRelatorioAction
  | RelatorioGeradoAction
  | CarregarDominioAction
  | CarregarMedidasUsuarioAction
  | AdicionarMedidaUsuarioAction
  | ExcluirMedidaUsuarioAction
  | ImportarMedidaUsuarioAction
  | SelecionarPeriodoAction
  | SelecionarPeriodicidadeAction
  | SelecionarAjusteDiasAction
  | SelecionarTipoListaPeriodosAction
  | SelecionarUltimaNegociacaoMesAction
  | SelecionarAdicaoDataReferenciaAction
  | AdicionarMedidaAction
  | AdicionarMedidaQualitativaAction
  | SelecionarTipoContaAction
  | SelecionarVersaoContaAction
  | SelecionarFormaAcumulacaoAction
  | SelecionarCapitalUtilizadoAction
  | ExportarFuncaoQtlink
  | ExportarFuncaoQtlinkSuccessAction
  | ExportarFuncaoQtlinkErroAction
  | SelecionarToleranciaDFAction
  | SelecionarAnosAction
  | SelecionarTipoReferenciaAction
  | SelecionarTrimestreReferenciaAction
  | SelecionarAnoReferenciaAction
  | SelecionarComparacaoAction
  | EditarLambdaAction
  | SelecionarAtivoSemRiscoAction
  | SelecionarTaxaDeAdministracaoAction
  | EditarHorizonteDuAction
  | EditarConfiancaAction
  | SelecionarVolatilidadeAction
  | SelecionarTipoDoRetornoAction
  | SelecionarBenchmarkAction
  | SelecionarPrimeiroAtivoSemRiscoAction
  | SelecionarPrimeiroBenchmarkAction
  | SelecionarCompetenciaAction
  | SelecionarPrimeiraCompetenciaAction
  | SelecionarFrequenciasAction
  | SelecionarPlUtilizadoAction
  | SelecionarToleranciaDiasValorMercadoAction
  | SelecionarCorretorMonetarioAction
  | SelecionarDeflatorAction
  | SelecionarValorBaseDeflatorAction
  | SelecionarValorBaseDeflatorOutroAction
  | SelecionarDataBaseDeflatorAction
  | SelecionarDataBaseDeflatorDataEspecificaAction
  | SelecionarIndiceAction
  | SelecionarIndicesAction
  | SelecionarPrimeiroIndiceAction
  | SelecionarDataEspecificaAction
  | SelecionarDataEspecificaDefaultAction
  | SelecionarDataInicialCompostaAction
  | SelecionarPrimeiraDataInicialCompostaAction
  | SelecionarDataFinalCompostaAction
  | SelecionarPrimeiraDataFinalCompostaAction
  | SelecionarDataInicialAction
  | SelecionarDataFinalAction
  | SelecionarAtivosCarteiraAction
  | SelecionarCamposAction
  | SelecionarCasasDecimaisMedidaAction
  | SelecionarExibirDatasSemNegociacaoSeriesAction
  | SelecionarIdentificadorSeriesAction
  | DeselecionarIdentificadorSeriesAction
  | SelecionarModoDeVisualizacaoSeriesAction
  | EditarCompradoresAction
  | ObterDatasUteisSelecaoJanelaAction
  | ObterDatasUteisSelecaoJanelaSuccessAction
  | ObterMesesSelecaoJanelaAction
  | ObterMesesSelecaoJanelaSuccessAction
  | SelecionarTamanhoDaJanelaAction
  | SelecionarQuantidadeJanelasAction
  | SelecionarTipoJanelaAction
  | SelecionarAtivosAction
  | SelecionarFiltrosTipoAtivoAction
  | DeselecionarFiltrosTipoAtivoAction
  | SelecionarGrupamentoMedidaAction
  | AbrirArquivoSuccessAction
  | AberturaDeArquivoCanceladaAction
  | AbrirPaginaArquivosAction
  | AbrirArquivoAction
  | SalvarArquivoAction
  | ArquivoSalvoAction
  | ErroAoSalvarArquivoAction
  | ErroAoAbrirArquivoAction
  | AbrirTemplateAction
  | ExcluirArquivoAction
  | LimparSelecaoAction
  | SelecionarAtivosAction
  | SelecionarFiltrosTipoAtivoAction
  | DeselecionarFiltrosTipoAtivoAction
  | SelecionarGrupamentoMedidaAction
  | TrocarMedidaDePosicaoAction
  | RestaurarNomePadraoDeMedidaSelecionadaAction
  | RenomearMedidaSelecionadaAction
  | MarcarMedidaSelecionadaAction
  | DesmarcarMedidaSelecionadaAction
  | MarcarTodasMedidasSelecionadasAction
  | DesmarcarTodasMedidaSelecionadasAction
  | ExcluirMedidasMarcadasAction
  | EditarMedidaAction
  | ExcluirMedidaAction
  | CarregarAppAction
  | IniciarAppAction
  | SetErroInicializacaoAction
  | DefineChavesI18nAction
  | SelecionarValorComboMedidasAction
  | SelecionarDataBaseAction
  | SelecionarDataBaseDefaultAction
  | SelecionarValorComboMedidasAction
  | ExportarExcelVinculadoAction
  | SetSeletorAbertoAction
  | ObterDetalhesUsuarioSuccessAction
  | SelecionaPesoRankingAction
  | SelecionaColunasRankingAction
  | SelecionaNomeMedidaCompostaAction
  | SelecionaFormatoAction
  | SelecionaExpressaoAction
  | SelecionarTipoCalculoAction
  | SelecionarFechamentoDeMesAction
  | SetMedidasSelecionadasDesabilitadoAction
  | SelecionarVisualizacaoCarteiraAction
  | AddReportRowsAction
  | AssetsAddedAction
  | AssetsRemovedAction
  | ExportReportAction
  | FetchReportAction
  | OpenAssetMenuAction
  | OpenChartsTabAction
  | OpenGlobalSearchAction
  | OpenNewTaskAction
  | RemoveAssetsAction
  | ReportExportedAction
  | ReportExportingErrorAction
  | ReportFetchedAction
  | ReportFetchingErrorAction
  | SaveAssetGroupAction
  | SaveAssetGroupErrorAction
  | SaveAssetGroupSuccessAction
  | SendMailAction
  | SetCurrentUserAction
  | SetReportLoadingAction
  | EditarLimiteAction
  | SelecionarRetornoNumeradorAction
  | SelecionarCalculoNumeradorAction
  | SelecionarPeriodicidadeEstatisticaNumeradorAction
  | EditarDiasUteisPeriodicidadeNumeradorAction
  | SelecionarTipoVolatilidadeAction
  | SelecionarEstimacaoVolatilidadeAction
  | SelecionarPeriodicidadeEstatisticaVolatilidadeAction
  | EditarDiasUteisPeriodicidadeVolatilidadeAction
  | SelecionarTipoRetornoEstatisticaAction
  | RestaurarConfiguracoesRelatorioAction
  | ConfiguracoesRestauradasAction
  | RestoreGridStateAction
  | ResetGridStateAction
  | SaveGridStateAction
  | SetIsAxisAction
  | SelecionarAjustesPrecoAction
  | SelecionarAjustesPrecoSemProventosAction
  | SelecionarEscalaMoedaAction
  | SelecionarFonteFidcAction
  | SelecionarExibirConfiguracoesAvancadasAction
  | SelecionarJanelaMovelAction
  | SelecionarConfiguracaoSeries
  | SetConfiguracoesRelatorioSalvoAction;

export type ActionCreator = any => Action;
